import type { FC } from "react";
import { Label, Radio, RadioGroup, Text } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { Size } from "../helpers/helpers.js";

interface RadioButtonOption {
  name: string;
  description?: string;
}

interface RadioButtonsGroupProps {
  label?: string;
  options: readonly RadioButtonOption[];
  value: string | null;
  onChange?: (newValue: string) => void;
  size?: typeof Size.MEDIUM | typeof Size.LARGE;
  isDisabled?: boolean;
}

export const RadioButtonsGroup: FC<RadioButtonsGroupProps> = ({
  label,
  options,
  value,
  onChange,
  size,
  isDisabled,
}) => {
  return (
    <RadioGroup
      isDisabled={Boolean(isDisabled)}
      onChange={(selectedValue) => {
        if (selectedValue && onChange) {
          onChange(selectedValue);
        }
      }}
      value={value}
    >
      {label ? (
        <Label className="block text-sm leading-6 font-medium text-foreground">
          {label}
        </Label>
      ) : null}
      <div
        className={twMerge("-space-y-px rounded-md bg-white", label && "mt-2")}
      >
        {options.map((option, index) => (
          <Radio
            className={({ isSelected, isFocusVisible }) =>
              twMerge(
                "relative flex items-center border focus:outline-hidden",
                index === 0 ? "rounded-tl-md rounded-tr-md" : "",
                index === options.length - 1
                  ? "rounded-br-md rounded-bl-md"
                  : "",
                isSelected
                  ? "z-10 border-indigo-200 bg-indigo-50"
                  : "border-border-light",
                size === Size.MEDIUM ? "p-2" : "p-2 sm:p-4",
                isDisabled ? "" : "cursor-pointer",
                isFocusVisible ? "z-10" : "",
              )
            }
            key={option.name}
            value={option.name}
          >
            {({ isSelected, isFocusVisible }) => (
              <>
                <span
                  aria-hidden="true"
                  className={twMerge(
                    "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border",
                    isSelected
                      ? "border-transparent bg-primary"
                      : "border-border-strong bg-white",
                    isFocusVisible
                      ? "ring-2 ring-indigo-600 ring-offset-2"
                      : "",
                    isDisabled ? "" : "cursor-pointer",
                  )}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-white" />
                </span>
                <span className="ml-3 flex flex-col">
                  <span
                    className={twMerge(
                      "block text-sm font-medium",
                      isSelected ? "text-indigo-900" : "text-foreground",
                    )}
                  >
                    {option.name}
                  </span>
                  {option.description ? (
                    <Text
                      slot="description"
                      className={twMerge(
                        "block text-sm",
                        isSelected
                          ? "text-indigo-700"
                          : "text-muted-foreground",
                      )}
                    >
                      {option.description}
                    </Text>
                  ) : null}
                </span>
              </>
            )}
          </Radio>
        ))}
      </div>
    </RadioGroup>
  );
};
